/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useContext, useEffect } from 'react';
import { Button, Form, FormGroup, Row, Col, Label, Input, InputGroup, InputGroupText } from 'reactstrap';
import Alert from 'react-s-alert';
import { Prompt } from 'react-router-dom';
import moment from 'moment';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Joyride, { STATUS } from 'react-joyride';

// import contexts
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// import services
import ClientService from '../../services/ClientService';
import AuthService from '../../services/AuthService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';
import ClientNavbar from './ClientNavbar';
import listOfStates from '../../assets/listOfStates.json';
import listOfCities from '../../assets/listOfCities.json';

const ClientProfile = props => {

    // contexts
    const { theme } = useContext(ThemeContext);
    const { user, setUser } = useContext(AuthContext);

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isDataModified, setIsDataModified] = useState(false);
    const [filteredCities, setFilteredCities] = useState(listOfCities);
    const [titles, setTitles] = useState([]);
    const [client, setClient] = useState({});
    const [otp, setOtp] = useState();
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [oldPhone, setOldPhone] = useState();
    const [disablePhone, setDisablePhone] = useState(true);
    const [minimumAge, setMinimumAge] = useState(0);
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>Manage your profile details from here!</h2>,
                placement: 'center',
                target: '#clientProfileDiv',
            },
            {
                content: <div className="tooltipDiv">Save your profile details by pressing this button.</div>,
                placement: 'right',
                target: '#saveBtn',
            }
        ]
    });
    let helpers = null;
    const [notifications, setNotifications] = useState({
        incompleteProfile: []
    });
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: ['Your profile is incomplete! Please complete that to enjoy our service.'],
            msgError: true
        }
    });

    // useEffects
    useEffect(() => {
        ClientService.getClientProfile()
            .then(data => {
                try {
                    if (!user.guideTakenFor.includes("ClientProfile")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    setTitles(data.siteVariable.userTitles);
                    setMinimumAge(data.siteVariable.minimumAgeForClient);
                    setOldPhone(data.client.phone);
                    setClient({
                        title: data.client.title,
                        firstName: data.client.firstName,
                        lastName: data.client.lastName,
                        email: data.client.email,
                        gender: data.client.gender,
                        birthDate: data.client.birthDate,
                        phone: data.client.phone,
                        profileImage: data.client.profileImage,
                        isPhoneVerified: data.client.user.isPhoneVerified,
                        address1: data.client.address1,
                        address2: data.client.address2,
                        pincode: data.client.pincode,
                        city: data.client.city,
                        state: data.client.state
                    });
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Client ClientProfile useEffect', 'Error in try catch block', err.stack);
                }
            });
        getNotifications();
        return () => {
            window.onbeforeunload = undefined
            setIsDataModified(false);
        }
    }, []);

    useEffect(() => {
        if (isDataModified) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined
        }
    }, [isDataModified]);

    const getNotifications = () => {
        AuthService.getNotifications().then(data => {
            try {
                let temp = { incompleteProfile: [] };
                Promise.all(data.notifications.ClientProfile.map(item => {
                    if (!item.readStatus && item.title === "incompleteProfile") {
                        temp.incompleteProfile.push(item.description);
                        setProfileComplete({ ...profileComplete, status: false });
                        AuthService.markNotificationAsRead("ClientProfile", item._id);
                    }
                })).then(() => {
                    setNotifications(temp);
                });
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Client Payment handleChange', 'Error in try catch block', err.stack);
            }
        })
    }

    // handle functions
    const handleChange = e => {
        try {
            setClient({ ...client, [e.target.name]: e.target.value });
            setIsDataModified(true);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handlePhoneChange = e => {
        try {
            if (oldPhone == e) {
                setClient({ ...client, isPhoneVerified: true, phone: e });
            } else {
                setIsOtpSent(false);
                setOtp();
                setClient({ ...client, isPhoneVerified: false, phone: e });
                setIsDataModified(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handlePhoneChange', 'Error in try catch block', err.stack);
        }
    }

    const openImageSelector = e => {
        document.getElementById("imageInput").click();
    }

    const handleImageChange = e => {
        try {
            let file = document.getElementById("imageInput").files[0];
            let reader = new FileReader();

            if (!file) {
                console.log("select file")
                return;
            } else if (file.size > 3e+6) {
                Alert.info("Please select file of size less then 3mb", {
                    position: 'top-right',
                    effect: 'slide'
                });
                return;
            }

            reader.readAsDataURL(file);

            reader.onloadend = () => {
                setClient({ ...client, profileImage: reader.result });
                setIsDataModified(true);
            };
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handleImageChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSentOtp = e => {
        try {
            setIsLoading(true);
            ClientService.sendOtpVerifyMobile('mobileVerification', client.phone)
                .then(data => {
                    setIsOtpSent(true);
                    setIsLoading(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Client ClientProfile handleSentOtp', 'Error in try catch block', err.stack);
        }
    }

    const handleCityChange = e => {
        try {
            if (e.target.selectedIndex == 0) {
                document.getElementById("state").selectedIndex = 0;
            } else {
                const index = e.target.selectedIndex - 1;
                const stateIndex = Number(filteredCities[index].state_id);
                document.getElementById("state").selectedIndex = stateIndex;
                setClient({ ...client, city: filteredCities[index].name });
                setIsDataModified(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handleCityChange', 'Error in try catch block', err.stack);
        }
    }

    const handleStateChange = e => {
        try {
            if (e.target.selectedIndex == 0) {
                setFilteredCities(listOfCities);
            } else {
                const index = e.target.selectedIndex - 1;
                setFilteredCities(listOfCities.filter(item => item.state_id == listOfStates[index].id));
                setClient({ ...client, state: listOfStates[index].name });
                setIsDataModified(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handleStateChange', 'Error in try catch block', err.stack);
        }
    }

    const handlePhoneVerify = e => {
        try {
            setIsLoading(true);
            const user = { token: otp, type: 'phone' };
            AuthService.verifyUser(user)
                .then(data => {
                    setIsOtpSent(false);
                    setDisablePhone(true);
                    setClient({ ...client, isPhoneVerified: true });
                    setIsDataModified(true);
                    setIsLoading(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                })
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Client ClientProfile handlePhoneVerify', 'Error in try catch block', err.stack);
        }
    }

    const handleEditPhone = e => {
        try {
            setDisablePhone(false);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client ClientProfile handleEditPhone', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading2(true);
            if (moment().diff(client.birthDate, 'years') >= minimumAge) {
                if (client.isPhoneVerified) {
                    ClientService.setClientProfile(client)
                        .then(data => {
                            setUser({ ...user, username: `${client.title} ${client.firstName} ${client.lastName}`, profileImage: client.profileImage });
                            setIsDataModified(false);
                            Alert.info(data.message.msgBody, {
                                position: 'top-right',
                                effect: 'slide'
                            });
                            setIsLoading2(false);
                        })
                        .catch(error => {
                            Alert.error(error.message.msgBody, {
                                position: 'top-right',
                                effect: 'slide'
                            });
                            setIsLoading2(false);
                        });
                } else {
                    Alert.error("Please verify your phone", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading2(false);
                }
            } else {
                Alert.error(`Minimum age required is ${minimumAge}`, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading2(false);
            GeneralService.logError('at Client ClientProfile handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("ClientProfile")) {
                AuthService.markVisited("ClientProfile");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "ClientProfile"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        <div>
            <Prompt
                when={isDataModified}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <ErrorBoundary errorAt="At Client ClientProfile Joyride">
                <Joyride
                    callback={handleJoyrideCallback}
                    continuous={true}
                    getHelpers={getHelpers}
                    run={tutorial.status}
                    scrollToFirstStep={true}
                    showProgress={false}
                    showSkipButton={false}
                    steps={tutorial.steps}
                    locale={{ last: 'Finish' }}
                    styles={{
                        options: {
                            primaryColor: "rgb(56, 78, 115)",
                            zIndex: 10000,
                        },
                    }}
                />
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Client ClientProfile ClientNavbar">
                <ClientNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            {profileComplete.status ? null : <Message message={profileComplete.message} />}
            <ErrorBoundary errorAt="At Client ClientProfile ProfileForm">
                <div id="clientProfileDiv" className="px-3 py-5">
                    <Form onSubmit={handleSubmit} style={{ paddingLeft: "10%", paddingRight: "10%" }}>
                        <h2 id="editProfile" style={theme.heading}>Edit Profile</h2>
                        <br />
                        <FormGroup>
                            <Label id="clientTitle" for="title" style={theme.formBodyText}>Title</Label>
                            <Input type="select" name="title" id="title" style={theme.formInput} onChange={handleChange} defaultValue={''} value={client.title} required>
                                <option value="">Select one</option>
                                {
                                    titles.map((item, index) => (
                                        <option key={index} value={item}>{item}</option>
                                    ))
                                }
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label id="clientfirstName" for="firstName" style={theme.formBodyText} >First Name</Label>
                            <Input id="clientfirstNameValue" type="text" name="firstName" value={client.firstName} onChange={handleChange} className="form-control" style={theme.formInput} required />
                        </FormGroup>

                        <FormGroup>
                            <Label id="clientlastName" for="lastName" style={theme.formBodyText} >Last Name </Label>
                            <Input id="clientlastNameValue" type="text" name="lastName" value={client.lastName} onChange={handleChange} className="form-control" style={theme.formInput} required />
                        </FormGroup>

                        <FormGroup>
                            <Label id="clientemail" for="email" style={theme.formBodyText} >Email </Label>
                            <Input id="clientemailinput" type="text" name="email" value={client.email} onChange={handleChange} className="form-control" disabled={true} style={theme.formInput} />
                        </FormGroup>

                        <FormGroup row className="ml-0">
                            <legend id="clientgender" style={theme.formBodyText}>Gender</legend>
                            <FormGroup check>
                                <Label check>
                                    <Input type="radio" name="gender" value="male" onChange={handleChange} checked={client.gender == "male" ? true : false} />{' '}
                                    Male
                                </Label>
                            </FormGroup>
                            <FormGroup className="ml-3" check>
                                <Label check>
                                    <Input type="radio" name="gender" value="female" onChange={handleChange} checked={client.gender == "female" ? true : false} />{' '}
                                    Female
                                </Label>
                            </FormGroup>
                        </FormGroup>

                        <FormGroup>
                            <Label id="clientbirthdate" for="birthDate" className="mt-2" style={theme.formBodyText} >Birthdate </Label>
                            <Input type="date" id="birthDate" name="birthDate" max={moment().format('YYYY-MM-DD')} value={client.birthDate} onChange={handleChange} className="form-control" style={theme.formInput} required />
                        </FormGroup>

                        <FormGroup>
                            <Label id="clientphonenumber" for="phone" style={theme.formBodyText} >Phone Number </Label><br />
                            <div className="d-flex">
                                <PhoneInput
                                    placeholder="+91 98765-43120"
                                    disabled={disablePhone}
                                    inputClass="form-control w-auto"
                                    inputStyle={theme.formInput}
                                    isValid={(value) => {
                                        if (value.slice(0, 2) !== "91") {
                                            return false;
                                        }
                                        return true;
                                    }}
                                    inputProps={{
                                        name: 'phone',
                                        required: true,
                                    }}
                                    country="in"
                                    disableDropdown={true}
                                    value={client.phone}
                                    enableSearch={true}
                                    onChange={handlePhoneChange} />
                                {
                                    disablePhone ?
                                        <Button id="editPhone" className="ml-2" style={theme.formButton} onClick={handleEditPhone}>Edit</Button>
                                        : null
                                }
                                {
                                    client.isPhoneVerified || isOtpSent ?
                                        null
                                        :
                                        <Button className="ml-2" style={theme.formButton} onClick={handleSentOtp} disabled={isLoading}>
                                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}
                                            Verify
                                        </Button>
                                }
                            </div>
                            {
                                client.isPhoneVerified ?
                                    <p className="my-1">
                                        <i className="fas fa-check-circle" style={{ color: "#384E73" }}></i> Phone is verified
                                    </p> : null
                            }
                            {
                                isOtpSent ?
                                    <div className="d-flex" style={{ marginTop: '5px' }}>
                                        <Input 
                                            type="text" 
                                            id="otp" 
                                            name="otp" 
                                            value={otp} 
                                            placeholder="Enter OTP" 
                                            onChange={(e) => setOtp(e.target.value)} 
                                            className="form-control" 
                                            style={theme.formInput} 
                                            required 
                                        />
                                        <Button 
                                            className="ml-2" 
                                            style={theme.formButton} 
                                            onClick={handlePhoneVerify} 
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}
                                            Submit
                                        </Button>
                                    </div>
                                    : null
                            }

                        </FormGroup>

                        <FormGroup>
                            <Label id="clientaddress" for="address1" style={theme.formBodyText}>Address</Label>
                            <Input type="text" style={theme.formInput} name="address1" id="address1" placeholder="Flat, House no. Building, Company, Apartment" onChange={handleChange} value={client.address1} required />
                        </FormGroup>
                        <FormGroup>
                            <Label id="clientaddress2" for="address2" style={theme.formBodyText}>Address 2</Label>
                            <Input type="text" style={theme.formInput} name="address2" id="address2" placeholder="Area, Colony, Street, Sector, Village" onChange={handleChange} value={client.address2} required />
                        </FormGroup>
                        <Row form>
                            <Col md={5}>
                                <FormGroup>
                                    <Label id="clientcity" for="city" style={theme.formBodyText}>City</Label>
                                    <Input type="select" id="city" name="city" style={theme.formInput} onChange={handleCityChange} value={client.city} required>
                                        <option value="">Select</option>
                                        {
                                            filteredCities.map((item) => (
                                                <option key={item.id} value={item.name}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label id="clientstate" for="state" style={theme.formBodyText}>State</Label>
                                    <Input type="select" id="state" name="state" style={theme.formInput} onChange={handleStateChange} value={client.state} required>
                                        <option value="">Select</option>
                                        {
                                            listOfStates.map((item) => (
                                                <option key={item.id} value={item.name}>{item.name}</option>
                                            ))
                                        }
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label id="clientpincode" for="Pincode" style={theme.formBodyText}>Pincode</Label>
                                    <Input type="text" style={theme.formInput} name="pincode" id="pincode" placeholder="123456" onChange={handleChange} value={client.pincode} required />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label id="clientCountry" for="country" style={theme.formBodyText}>Country</Label>
                            <Input type="text" name="country" id="country" disabled value="India" style={theme.formInput} required />
                        </FormGroup>

                        <label id="clietnprofileimage" htmlFor="profileImageDiv" style={theme.formBodyText} >Profile Image </label>
                        <div className="row" id="profileImageDiv">
                            <div style={{ width: "120px", height: "120px" }}>
                                <img className="m-auto w-100 h-100" id="profileImage" src={client.profileImage} alt="Profile Image" style={{ borderRadius: "100%", border: "solid 3px #C4C4C4" }} />
                            </div>
                            <button id="clienteditbtn" className="btn mt-auto ml-3"
                                type="button"
                                onClick={openImageSelector}
                                style={Object.assign({ height: 'fit-content' }, theme.formButton)}>Edit</button>
                            <div style={{ visibility: 'hidden', height: '0px' }}>
                                <input type="file" id="imageInput" accept=".jpg, .jpeg, .png" onChange={handleImageChange} />
                            </div>
                        </div>
                        <br />
                        <button id="saveBtn" className="btn" type="submit" style={isDataModified ? theme.buttonWarning : theme.button} disabled={isLoading2}>
                            {isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save Changes
                        </button>

                    </Form>
                </div>
            </ErrorBoundary>
        </div>
    )

}

export default ClientProfile;